import { useMemo } from "react";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import { Text } from "@ui-component-library";
import MediaSelector from "../editor/fields/MediaSelector";
import DroneVideo from "../commons/DroneVideo";
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import { getYouTubeVideoId } from "../../helpers/youtube";
import HeroText from "./HeroText";

const FeatureSection = (props) => {
  const youtubeVideoId = useMemo(
    () => getYouTubeVideoId(props.videoUrl),
    [props.videoUrl]
  );

  const items = [
    {
      title: props.itemOneTitle,
      text: props.itemOneText,
    },
    {
      title: props.itemTwoTitle,
      text: props.itemTwoText,
    },
    {
      title: props.itemThreeTitle,
      text: props.itemThreeText,
    }
  ];

  return (
    <TailwindWrapper>
      <section className="w-full py-[48px] lg:py-[96px] container">
        {(props.title || props.text) && <div className="mb-12">
          <HeroText variant="embedded" sectionName='' title={props.title} text={props.text} />
        </div>}

        <div className="w-full flex justify-center items-center">
          {props.mediaType === "youtube" && (
            <div className="rounded-xl overflow-hidden w-full h-full">
              {youtubeVideoId ? (
                <LiteYouTubeEmbed
                  id={youtubeVideoId}
                  title={props.videoTitle || "Video"}
                  poster="maxresdefault"
                />
              ) : (
                <div className="aspect-video w-full rounded-xl bg-gray-200 flex items-center justify-center text-gray-500 text-sm p-4">
                  Add a YouTube URL or video ID
                </div>
              )}
            </div>
          )}
          {props.mediaType === "videoUrl" && (
            <div className="h-full aspect-video bg-gray-100 rounded-xl border border-gray-200 overflow-hidden">
              {props.videoUrl && (
                <div className={"flex-grow-1 overflow-hidden h-full"}>
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    className={`rounded-4 img-fluid w-full h-full object-cover`}
                  >
                    <source
                      src={`${Window.static_path}video/${props.videoUrl}`}
                      type="video/mp4"
                    />
                  </video>
                </div>
              )}
              {!props.videoUrl && <p>Please add url to uploaded video</p>}
            </div>
          )}
          {props.mediaType === "droneVideo" && (
            <div className="w-full aspect-video bg-gray-100 rounded-xl border border-gray-200 relative overflow-hidden">
              <div className="absolute inset-0 w-full h-full">
                <DroneVideo
                  fileName={"copture_video_1080p.mp4"}
                  fileNameSmall={"copture_video_360.mp4"}
                />
              </div>
            </div>
          )}
          {props.mediaType === "image" && (
            <div className="w-full aspect-video bg-gray-100 rounded-xl border border-gray-200 relative overflow-hidden">
              <img
                alt={props.alt || "Hero image"}
                src={props.src || "https://placehold.co/1200x1200"}
                className={"w-full h-full object-cover vb-rounded"}
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap lg:flex-nowrap gap-12 lg:gap-6 mt-12">
          {items.map((item, index) => (
            <div key={index} className="w-full lg:w-1/3">
              <Text tw variant="text-base/leading-normal/medium" className="mb-2">
                {item.title}
              </Text>
              <Text tw variant="text-base/leading-normal/normal" className="text-[#737373]">
                {item.text}
              </Text>
            </div>
          ))}
        </div>          
      </section>
    </TailwindWrapper>
  );
};

FeatureSection.defaultProps = {
  mediaType: "image",
  src: "https://placehold.co/1200x900",
  alt: "scenic image",
  title: "Headline that solves user's main problem",
  text: "Follow with one or two sentences that expand on your value proposition. Focus on key benefits and address why users should take action now. Keep it scannable, direct and benefit-driven.",
  itemOneTitle: "Feature one",
  itemOneText: "Follow with one or two sentences that expand on your value proposition. Focus on key benefits and address why users should take action now.",
  itemTwoTitle: "Feature two",
  itemTwoText: "Follow with one or two sentences that expand on your value proposition. Focus on key benefits and address why users should take action now.",
  itemThreeTitle: "Feature three",
  itemThreeText: "Follow with one or two sentences that expand on your value proposition. Focus on key benefits and address why users should take action now."
};

FeatureSection.resolveFields = (data) => {
  const fields = {
    title: {
      type: "text",
      label: "Title",
    },
    text: {
      type: "textarea",
      label: "Description Text",
    },
    itemOneTitle: {
      type: "text",
      label: "Item One Title",
    },
    itemOneText: {
      type: "textarea",
      label: "Item One Text",
    },
    itemTwoTitle: {
      type: "text",
      label: "Item Two Title",
    },
    itemTwoText: {
      type: "textarea",
      label: "Item Two Text",
    },
    itemThreeTitle: {
      type: "text",
      label: "Item Three Title",
    },
    itemThreeText: {
      type: "textarea",
      label: "Item Three Text",
    },
    mediaType: {
      type: "select",
      label: "Media Type",
      options: [
        { label: "Image", value: "image" },
        { label: "Drone Video", value: "droneVideo" },
        { label: "Video URL", value: "videoUrl" },
        { label: "YouTube", value: "youtube" },
      ],
    },
  };

  // Conditional field that's show depending on the media type field above
  if (data.props.mediaType === "image") {
    return {
      ...fields,
      src: {
        type: "custom",
        label: "Image Source",
        render: ({ name, onChange, value }) => (
          <MediaSelector
            name={name}
            label="Image Source"
            type={"img"}
            onChange={onChange}
            value={value}
          />
        ),
      },
      alt: {
        type: "text",
        label: "Image Alt Text",
      },
    };
  }

  // Conditional field that's show depending on the media type field above
  if (data.props.mediaType === "videoUrl") {
    return {
      ...fields,
      videoUrl: {
        type: "text",
        label: "Video URL",
      },
    };
  }

  if (data.props.mediaType === "youtube") {
    return {
      ...fields,
      videoUrl: {
        type: "text",
        label: "Video URL or ID",
      },
      videoTitle: {
        type: "text",
        label: "Custom video title",
      },
    };
  }

  return fields;
};

export default FeatureSection;
